import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authHeaders } from "../../lib/auth";
import { fetchHealth } from "../../lib/api";
import {
  BuilderChat,
  ContractCard,
  DrillDrawer,
  EvidenceLines,
  PropertyRow,
  type ChatTurn,
} from "../builder/components";
import { deriveContract } from "../builder/contract-view";
import { CreateFlow } from "../builder/create-flow";
import { parseScheduleZh } from "../../lib/parse-schedule";
import { SkillsManager } from "../../skills/SkillsManager";

/**
 * 助手配置 console section (agent-profile M1/M3/M4/M5, P1 right-column scope).
 * Every user-visible string follows the design's §09 canonical copy table
 * verbatim (docs/design/2026-07-02-configurable-vertical-agent-profile.html).
 * M1 = list + edit form with provenance tags (来源层 is a first-class citizen);
 * M3 = publish confirm「草稿 → 版本 {N} · 新对话起生效」with human-language
 * validation errors; M4 =「助手绑定与运行状况」read-only strip; M5 =「发布记录」
 * with 展开变更 and republish-as-new-version 回滚.
 */

interface ProfileListItem {
  id: string;
  published: boolean;
  version: number;
  name: string;
  isDefault: boolean;
  /** P2d-4b: taken off the shelf (home hides it; restorable). */
  archived?: boolean;
}

interface PublishedView {
  identity: { name: string; tagline?: string };
  instructions: { role: string; whenUnsure?: string; outputFormat?: string; rules?: string };
  capabilities: {
    /** Author-written WILL lines (contract card; derived when absent). */
    will?: string[];
    wont: string[];
    tokenCaps: { maxAnswerTokens?: number };
    budget?: { perExecution?: number; daily?: number };
  };
  visibility?: { minClearance?: string };
  skills: string[];
  kbFilter: { sources: string[] };
  /** Assistants this one may consult / transfer to on its own (§5 协作). */
  collaborators?: string[];
  /** P2e: named connections bound to this assistant (feishu:/git: ids). */
  connectors?: string[];
  version: number;
}

/** Three-axis state of one declared skill (§10: 已声明 · 因数据权限未生效). */
interface SkillState {
  id: string;
  state: "effective" | "blocked";
  reason?: "clearance";
}

/** P2: one 待采纳 field proposal (server-validated before it ever appears). */
interface Proposal {
  id: string;
  field: string;
  after: string | string[];
  summary: string;
  source: "chat" | "field-rewrite";
}

/** Draft layer as returned by the detail read (partial overlay). */
interface DraftLayer {
  identity?: { name?: string; tagline?: string };
  instructions?: { role?: string; whenUnsure?: string; outputFormat?: string; rules?: string };
  capabilities?: { wont?: string[] };
  collaborators?: string[];
  connectors?: string[];
}

interface ProfileDetail {
  id: string;
  published?: PublishedView;
  draft?: DraftLayer;
  /** The authoring layer source — the edit base the form must PRESERVE. */
  authoring?: Record<string, unknown>;
  versions: number[];
  /** 来源层 per top-level field (§07 M1: 基座 / 本企业; 行业模板 arrives P2). */
  provenance?: Record<string, string>;
  /** Server-computed per-skill effectiveness (mirrors the runtime clearance gate). */
  skillStates?: SkillState[];
  /** P2: pending 待采纳 proposals (present when the builder flag is on). */
  proposals?: Proposal[];
  /** P2b optimistic locking: echoed back on the next draft save. */
  draftRev?: number;
}

interface VersionRecord {
  version: number;
  publishedRecord: {
    subjectId: string | null;
    payload: { diff?: { field: string }[]; rollbackOf?: number };
  } | null;
}

interface RegistryInfo {
  registry: Record<string, { profiles: string[]; default: string }>;
  /** Where the live table came from: registry.yaml (editable home) or the env seed. */
  source?: "file" | "env";
  /** Published profile ids — the binding picker's universe. */
  profiles: string[];
  fallbackCounts: Record<string, number>;
  conversations7d: Record<string, number>;
  unboundTenants: string[];
  /** P2c metering (builder flag on): per-binding today-spend vs daily budget. */
  budgets?: {
    enforce: "alert" | "refuse";
    rows: {
      tenant: string;
      profileId: string;
      todaySpendCny: number;
      dailyBudgetCny?: number;
      overBudget: boolean;
    }[];
  };
}

/** P2d evidence payload (GET /api/profiles/:id/evidence — design spec §05). */
interface EvidenceInfo {
  connectors: {
    id: string;
    label: string;
    kind: "feishu" | "git";
    state: "connected" | "expired" | "unknown";
    checkedAt: number | null;
    permissions: string;
  }[];
  kb: { filter: string[]; sources: number; chunks: number } | null;
  spend: { todayCny: number; dailyBudgetCny?: number; overBudget: boolean } | null;
}

/** §09: field summaries and validation errors speak Chinese field names. */
const FIELD_NAMES: Record<string, string> = {
  identity: "名称与简介",
  instructions: "角色说明",
  capabilities: "能力与边界",
  skills: "能力",
  kbFilter: "知识范围",
  visibility: "可见范围",
  connectors: "连接",
  collaborators: "协作"
};
const fieldName = (f: string): string => FIELD_NAMES[f.split(".")[0] ?? f] ?? f;

/** §07 M1 mock: skills speak human names in the 能力 row (raw id as fallback). */
const SKILL_NAMES: Record<string, string> = {
  search_kb: "资料检索",
  code_qa: "源码问答",
  create_ticket: "建工单",
  web_search: "联网搜索"
};
const skillName = (id: string): string => {
  if (id.startsWith("ext:")) {
    // ext:{tenant}/{name} → the uploaded skill's own name (admin uploaded it,
    // the tenant prefix is plumbing, not identity).
    const slash = id.indexOf("/");
    return slash >= 0 ? id.slice(slash + 1) : id;
  }
  return SKILL_NAMES[id] ?? id;
};

/** §09 canonical hover copy for a clearance-blocked skill row (design row 4). */
const BLOCKED_SKILL_HINT = "当前企业数据权限不含内部资料，此能力不出现在对话中";

/** P2 proposal fields → the M1 field labels (§09 编辑面字段标签). */
const PROPOSAL_FIELD_NAMES: Record<string, string> = {
  "identity.name": "名称",
  "identity.tagline": "简介",
  "instructions.role": "角色说明",
  "instructions.whenUnsure": "拿不准时",
  "instructions.outputFormat": "输出格式",
  "instructions.rules": "规则",
  "capabilities.wont": "边界（不做）"
};

/** §09 发布确认 · 校验错误 — the merge policies in human language. */
function violationCopy(v: { field: string; message: string }): string {
  if (v.field === "version" || v.field === "instructions") return v.message; // 已是人话
  if (v.field.startsWith("kbFilter")) return "知识范围超出了上层允许的集合，调整后才能发布";
  if (v.field.startsWith("capabilities.wont")) return "边界只能新增，不能移除";
  if (v.field.startsWith("capabilities.tokenCaps") || v.field.startsWith("capabilities.budget"))
    return "上限只能收紧，不能放宽";
  if (v.field.startsWith("visibility")) return "可见范围只能提高，不能放宽";
  if (v.field.startsWith("skills")) return "能力超出了可用范围，调整后才能发布";
  return `${fieldName(v.field)}：${v.message}`;
}

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { headers: authHeaders({ Accept: "application/json" }) });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export function ProfilesSection(): JSX.Element {
  // P2d-4 two-level IA: /admin/assistants = home (cards), /:profileId = detail.
  // Flag off ignores the routing split and renders the P1 single page.
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const [detailTab, setDetailTab] = useState<"overview" | "connect" | "bindings" | "usage" | "versions">("overview");
  const [list, setList] = useState<ProfileListItem[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProfileDetail | null>(null);
  const [records, setRecords] = useState<VersionRecord[]>([]);
  const [registry, setRegistry] = useState<RegistryInfo | null>(null);
  // M1 form state (P1 right column; schema-aligned field set).
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [role, setRole] = useState("");
  const [whenUnsure, setWhenUnsure] = useState("");
  const [outputFormat, setOutputFormat] = useState("");
  const [rules, setRules] = useState("");
  const [wont, setWont] = useState("");
  // §5 协作: assistants this one may autonomously consult / transfer to.
  const [collaborators, setCollaborators] = useState<string[]>([]);
  // P2e: named connections bound to this assistant (真挑选 — code_qa scope 等).
  const [boundConnectors, setBoundConnectors] = useState<string[]>([]);
  // The loaded edit base (draft ?? authoring): the form MERGES over it so
  // layer fields it does not edit (kbFilter, budget, starters, …) survive a
  // UI save/publish (they used to be silently dropped — 2026-07-06 fix).
  const layerBaseRef = useRef<Record<string, unknown>>({});
  const [confirming, setConfirming] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // P2b: the draft rev this form loaded at; echoed on save, updated on 200.
  const draftRevRef = useRef(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [violations, setViolations] = useState<string[]>([]);
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);

  // P2 builder surfaces (preview sandbox) — gated on features.agentBuilder.
  const [builderOn, setBuilderOn] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQ, setPreviewQ] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewA, setPreviewA] = useState<{ text: string; citations: number } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  useEffect(() => {
    void fetchHealth().then((r) => {
      if (r.kind === "ok") setBuilderOn(r.health.features?.agentBuilder === true);
    });
  }, []);

  // M4 dynamic binding: the tenant row being edited + its draft binding.
  const [bindingTenant, setBindingTenant] = useState<string | null>(null);
  const [bindingProfiles, setBindingProfiles] = useState<string[]>([]);
  const [bindingDefault, setBindingDefault] = useState("");
  const [bindingErrors, setBindingErrors] = useState<string[]>([]);
  const [bindingNotice, setBindingNotice] = useState<string | null>(null);
  const [confirmUnbind, setConfirmUnbind] = useState(false);

  const [loadError, setLoadError] = useState(false);
  const refresh = useCallback(async () => {
    const body = await getJson<{ profiles: ProfileListItem[] }>("/api/profiles");
    const reg = await getJson<RegistryInfo>("/api/profiles/admin/registry");
    setLoadError(body === null || reg === null);
    setList(body?.profiles ?? []);
    setRegistry(reg);
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Load one profile into the edit/detail state (shared by both IA levels). */
  const loadDetail = useCallback(async (id: string) => {
    setSelected(id);
    setNotice(null);
    setViolations([]);
    setSavedAt(null);
    setExpandedVersion(null);
    // P2d: a fresh selection starts a fresh builder conversation (spec §03
    // rule 4) and re-reads the evidence backing the living document.
    setChatTurns([]);
    setDrawerField(null);
    setOpenConnector(null);
    setEvidence(null);
    void getJson<EvidenceInfo>(`/api/profiles/${id}/evidence`).then(setEvidence);
    const d = await getJson<ProfileDetail>(`/api/profiles/${id}`);
    setDetail(d);
    const recs = await getJson<{ versions: VersionRecord[] }>(`/api/profiles/${id}/versions`);
    setRecords(recs?.versions ?? []);
    // Draft-over-published: an autosaved draft (or an adopted proposal) is
    // what the admin was working on — the form must show it, not silently
    // reset to the published values.
    const p = d?.published;
    const dr = d?.draft;
    draftRevRef.current = d?.draftRev ?? 0;
    setName(dr?.identity?.name ?? p?.identity.name ?? id);
    setTagline(dr?.identity?.tagline ?? p?.identity.tagline ?? "");
    setRole(dr?.instructions?.role ?? p?.instructions.role ?? "");
    setWhenUnsure(dr?.instructions?.whenUnsure ?? p?.instructions.whenUnsure ?? "");
    setOutputFormat(dr?.instructions?.outputFormat ?? p?.instructions.outputFormat ?? "");
    setRules(dr?.instructions?.rules ?? p?.instructions.rules ?? "");
    setWont((dr?.capabilities?.wont ?? p?.capabilities.wont ?? []).join("\n"));
    setCollaborators(dr?.collaborators ?? p?.collaborators ?? []);
    setBoundConnectors(dr?.connectors ?? p?.connectors ?? []);
    layerBaseRef.current = ((dr as Record<string, unknown> | undefined) ??
      d?.authoring ??
      {});
  }, []);

  /**
   * Open an assistant. Builder IA navigates to the detail route (same id =
   * in-place reload, used by publish/adopt to refresh); the P1 page keeps its
   * single-page inline selection.
   */
  const open = useCallback(
    async (id: string) => {
      if (builderOn) {
        if (profileId === id) {
          await loadDetail(id);
          return;
        }
        setDetailTab("overview");
        navigate(`/admin/assistants/${id}`);
        return;
      }
      await loadDetail(id);
    },
    [builderOn, profileId, navigate, loadDetail]
  );

  // Detail route → load; back to home → clear the selection.
  useEffect(() => {
    if (builderOn && profileId !== undefined) {
      void loadDetail(profileId);
    } else if (profileId === undefined) {
      setSelected(null);
    }
  }, [builderOn, profileId, loadDetail]);

  /**
   * The authoring layer the current form state describes — MERGED over the
   * loaded edit base so layer fields the UI does not edit (kbFilter, budget,
   * starters, …) survive a save/publish. Building it from scratch used to
   * silently drop them (2026-07-06 fix; camp's collaborators/kbFilter were
   * one UI publish away from vanishing).
   */
  const formLayer = useCallback((): Record<string, unknown> => {
    const base = layerBaseRef.current;
    const rec = (v: unknown): Record<string, unknown> =>
      typeof v === "object" && v !== null ? { ...(v as Record<string, unknown>) } : {};
    const identity = rec(base.identity);
    identity.name = name;
    if (tagline.length > 0) identity.tagline = tagline;
    else delete identity.tagline;
    const instructions = rec(base.instructions);
    instructions.role = role;
    for (const [k, v] of [
      ["whenUnsure", whenUnsure],
      ["outputFormat", outputFormat],
      ["rules", rules],
    ] as const) {
      if (v.length > 0) instructions[k] = v;
      else delete instructions[k];
    }
    const capabilities = rec(base.capabilities);
    capabilities.wont = wont
      .split("\n")
      .map((w) => w.trim())
      .filter((w) => w.length > 0);
    const layer: Record<string, unknown> = {
      ...base,
      id: selected,
      identity,
      instructions,
      capabilities,
      ...(detail?.published !== undefined ? { skills: detail.published.skills } : {}),
    };
    if (collaborators.length > 0) layer.collaborators = collaborators;
    else delete layer.collaborators;
    if (boundConnectors.length > 0) layer.connectors = boundConnectors;
    else delete layer.connectors;
    return layer;
  }, [selected, name, tagline, role, whenUnsure, outputFormat, rules, wont, collaborators, boundConnectors, detail]);

  const saveDraft = useCallback(async () => {
    if (selected === null) return;
    try {
      const res = await fetch(`/api/profiles/${selected}/draft`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ ...formLayer(), rev: draftRevRef.current })
      });
      if (res.status === 409) {
        // P2b: someone else saved this draft — reload theirs rather than
        // silently clobbering (the server refused the stale rev).
        const body = (await res.json()) as { message?: string };
        setNotice(body.message ?? "草稿已被别人更新，已为你加载最新内容");
        await open(selected);
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { rev?: number };
      if (typeof body.rev === "number") draftRevRef.current = body.rev;
      setSavedAt("刚刚");
    } catch {
      setNotice("保存失败，请稍后再试");
    }
  }, [selected, formLayer, open]);

  /** Whether the form differs from the published snapshot (M3 empty state). */
  const changedFields = useCallback((): string[] => {
    const pub = detail?.published;
    if (pub === undefined) return ["identity", "instructions"];
    const out: string[] = [];
    if (name !== pub.identity.name || tagline !== (pub.identity.tagline ?? ""))
      out.push("identity");
    if (
      role !== pub.instructions.role ||
      whenUnsure !== (pub.instructions.whenUnsure ?? "") ||
      outputFormat !== (pub.instructions.outputFormat ?? "") ||
      rules !== (pub.instructions.rules ?? "")
    )
      out.push("instructions");
    if (wont !== pub.capabilities.wont.join("\n")) out.push("capabilities.wont");
    if (collaborators.join(",") !== (pub.collaborators ?? []).join(",")) out.push("collaborators");
    if (boundConnectors.join(",") !== (pub.connectors ?? []).join(",")) out.push("connectors");
    return out;
  }, [detail, name, tagline, role, whenUnsure, outputFormat, rules, wont, collaborators, boundConnectors]);

  const publish = useCallback(async () => {
    if (selected === null) return;
    setConfirming(false);
    setViolations([]);
    try {
      const res = await fetch(`/api/profiles/${selected}/publish`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          authoring: formLayer(),
          // M3 version precheck: the version this confirm's diff was computed
          // against; a concurrent publish → 409 with a re-review message.
          expectedVersion: detail?.published?.version ?? 0
        })
      });
      const body = (await res.json()) as {
        version?: number;
        violations?: { field: string; message: string }[];
      };
      if (!res.ok) {
        // Stale confirm: reload the fresh published state FIRST (open resets
        // the violation list), then surface the re-review message.
        if (res.status === 409) await open(selected);
        setViolations((body.violations ?? []).map(violationCopy));
        return;
      }
      setNotice("已发布 · 新对话起生效");
      await refresh();
      await open(selected);
    } catch {
      setNotice("发布失败，请重试");
    }
  }, [selected, formLayer, detail, refresh, open]);

  const rollback = useCallback(
    async (version: number) => {
      if (selected === null) return;
      const res = await fetch(`/api/profiles/${selected}/rollback`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ version })
      });
      if (res.ok) {
        setNotice("已发布 · 新对话起生效");
        await refresh();
        await open(selected);
      }
    },
    [selected, refresh, open]
  );

  // §09: admin surfaces speak assistant NAMES, not ids (mono id only as a
  // disambiguator when two assistants share a name, e.g. loom/default 小环).
  const nameOf = useCallback(
    (id: string): string => {
      const item = list.find((p) => p.id === id);
      if (item === undefined) return id;
      const dup = list.filter((p) => p.name === item.name).length > 1;
      return dup ? `${item.name}（${id}）` : item.name;
    },
    [list]
  );

  // ---- M4 dynamic binding handlers ----------------------------------------
  const openBinding = useCallback(
    (tenant: string, b?: { profiles: string[]; default: string }) => {
      setBindingTenant(tenant);
      setBindingProfiles(b?.profiles ?? []);
      setBindingDefault(b?.default ?? "");
      setBindingErrors([]);
      setBindingNotice(null);
      setConfirmUnbind(false);
    },
    []
  );

  const toggleBindingProfile = useCallback((id: string) => {
    setBindingProfiles((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    // Dropping the current default clears the selection (must re-pick).
    setBindingDefault((prev) => (prev === id ? "" : prev));
  }, []);

  const saveBinding = useCallback(async () => {
    if (bindingTenant === null) return;
    setBindingErrors([]);
    try {
      const res = await fetch(`/api/profiles/admin/registry/${bindingTenant}`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ profiles: bindingProfiles, default: bindingDefault })
      });
      const body = (await res.json()) as { violations?: { message: string }[] };
      if (!res.ok) {
        setBindingErrors((body.violations ?? [{ message: "保存失败，请重试" }]).map((v) => v.message));
        return;
      }
      setBindingTenant(null);
      setBindingNotice("已更新绑定 · 新对话起生效");
      await refresh();
    } catch {
      setBindingErrors(["保存失败，请重试"]);
    }
  }, [bindingTenant, bindingProfiles, bindingDefault, refresh]);

  const unbind = useCallback(async () => {
    if (bindingTenant === null) return;
    const res = await fetch(`/api/profiles/admin/registry/${bindingTenant}`, {
      method: "DELETE",
      headers: authHeaders({ Accept: "application/json" })
    });
    if (res.ok) {
      setBindingTenant(null);
      setBindingNotice("已解除绑定 · 该企业改由默认助手服务");
      await refresh();
    }
  }, [bindingTenant, refresh]);

  // P2 新建助手 stepper (design 旅程 D): 描述 → 一次生成 → 四步确认 → 发布。
  const [creating, setCreating] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [createDesc, setCreateDesc] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [genLayer, setGenLayer] = useState<Record<string, unknown> | null>(null);
  const [createId, setCreateId] = useState("");
  const [createName, setCreateName] = useState("");
  const [createTagline, setCreateTagline] = useState("");
  const [createSkills, setCreateSkills] = useState<string[]>([]);
  const [skillUniverse, setSkillUniverse] = useState<string[]>([]);

  // P2d-2: the tenant's connector inventory rides the create flow's STEP 2 —
  // rendered FROM the evidence data, never a hardcoded claim (2026-07-06
  // review lesson: static copy about live systems has no drift gate).
  const [createConnectors, setCreateConnectors] = useState<
    { id: string; label: string; state: "connected" | "expired" | "unknown" }[] | null
  >(null);
  // P2d-3 (review): STEP 4 is REAL scheduling — the NL phrase parses to a cron
  // client-side (the scheduler wizard's own parser) and publish creates the task.
  const [createSchedule, setCreateSchedule] = useState("");
  // P2e: connectors picked in STEP 2, carried into the create publish.
  const [createBoundConnectors, setCreateBoundConnectors] = useState<string[]>([]);
  const createScheduleParsed = parseScheduleZh(createSchedule);

  const startCreate = useCallback(() => {
    setCreating(true);
    setCreateStep(0);
    setCreateDesc("");
    setCreateErr(null);
    setGenLayer(null);
    setCreateConnectors(null);
    setCreateBoundConnectors([]);
    // Connector evidence is tenant-scoped; the always-published default
    // profile is the stable read path for it.
    void getJson<EvidenceInfo>("/api/profiles/default/evidence").then((ev) =>
      setCreateConnectors(
        (ev?.connectors ?? []).map((c) => ({ id: c.id, label: c.label, state: c.state }))
      )
    );
  }, []);

  /**
   * One bounded generation (shared by 开始搭建 and the refine composer).
   * keepConfirmed: cards the admin already confirmed survive a regeneration —
   * only the not-yet-confirmed cards adopt the new outcome (spec §02).
   */
  const runGenerate = useCallback(
    async (description: string, keepConfirmed: boolean) => {
      setCreateBusy(true);
      setCreateErr(null);
      try {
        const res = await fetch("/api/profiles/new/generate", {
          method: "POST",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ description })
        });
        const body = (await res.json()) as {
          suggestedId?: string;
          layer?: {
            identity: { name: string; tagline?: string };
            skills: string[];
          } & Record<string, unknown>;
          universe?: string[];
          message?: string;
        };
        if (!res.ok || body.layer === undefined || body.suggestedId === undefined) {
          setCreateErr(body.message ?? "没搭出来，换个说法试试");
          return;
        }
        setGenLayer(body.layer);
        if (!keepConfirmed || createStep <= 1) {
          setCreateId(body.suggestedId);
          setCreateName(body.layer.identity.name);
          setCreateTagline(body.layer.identity.tagline ?? "");
        }
        if (!keepConfirmed || createStep <= 3) {
          setCreateSkills(body.layer.skills);
        }
        // The picker universe comes from the SERVER (built-ins ∪ the tenant's
        // uploaded ext skills). Reconstructing it client-side silently dropped
        // ext skills and made upload look unsupported (review 2026-07-06).
        if (body.universe !== undefined) {
          setSkillUniverse([...new Set([...body.universe, ...body.layer.skills])]);
        } else {
          const d = await getJson<ProfileDetail>("/api/profiles/default");
          setSkillUniverse([...new Set([...(d?.published?.skills ?? []), ...body.layer.skills])]);
        }
        if (createStep === 0) setCreateStep(1);
      } catch {
        setCreateErr("没搭出来，换个说法试试");
      } finally {
        setCreateBusy(false);
      }
    },
    [createStep]
  );

  const genAssistant = useCallback(async () => {
    if (createDesc.trim().length === 0) return;
    await runGenerate(createDesc, false);
  }, [createDesc, runGenerate]);

  /** 说话改卡 (spec §02): the refinement joins the accumulated description. */
  const refineCreate = useCallback(
    async (text: string) => {
      const desc = `${createDesc}\n补充要求：${text}`;
      setCreateDesc(desc);
      await runGenerate(desc, true);
    },
    [createDesc, runGenerate]
  );

  const publishCreate = useCallback(async () => {
    if (genLayer === null || createId.trim().length === 0) return;
    setCreateBusy(true);
    setCreateErr(null);
    try {
      const res = await fetch(`/api/profiles/${createId}/publish`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          authoring: {
            ...genLayer,
            id: createId,
            identity: {
              name: createName,
              ...(createTagline.length > 0 ? { tagline: createTagline } : {}),
              ...((genLayer.identity as { starters?: string[] }).starters !== undefined
                ? { starters: (genLayer.identity as { starters?: string[] }).starters }
                : {})
            },
            skills: createSkills,
            ...(createBoundConnectors.length > 0 ? { connectors: createBoundConnectors } : {})
          },
          // creation = publish against "nothing published"; an existing id
          // 409s here, doubling as the collision check.
          expectedVersion: 0
        })
      });
      const body = (await res.json()) as { violations?: { field: string; message: string }[] };
      if (!res.ok) {
        setCreateErr(
          res.status === 409
            ? "这个标识已被占用，换一个再发布"
            : (body.violations ?? []).map(violationCopy).join("；") || "发布失败，请重试"
        );
        return;
      }
      // STEP 4 was real: the parsed schedule becomes an actual agent.prompt
      // task (same contract as the scheduler wizard) — managed on 日程 page.
      let scheduleNote = "";
      const parsedSchedule = parseScheduleZh(createSchedule);
      if (parsedSchedule !== null) {
        const taskRes = await fetch("/api/scheduler/tasks", {
          method: "POST",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            title: `${createName} · 定时`,
            jobType: "agent.prompt",
            cronExpression: parsedSchedule.cron,
            permission: "assist",
            prompt: createSchedule,
            // P2e semantic #3: the assistant's bound connectors become the
            // task's grants — the task runs with exactly what its assistant
            // was given (feishu push targets, git repos), nothing implicit.
            ...(createBoundConnectors.length > 0
              ? { jobParams: { connectors: createBoundConnectors } }
              : {}),
          }),
        }).catch(() => null);
        scheduleNote =
          taskRes?.ok === true
            ? ` · 定时任务已创建（${parsedSchedule.label}）`
            : " · 定时任务创建失败，请到「日程」页手动补";
      }
      setCreating(false);
      setCreateSchedule("");
      setNotice(`已发布 · 绑定到企业后用户可见${scheduleNote}`);
      await refresh();
      await open(createId);
    } catch {
      setCreateErr("发布失败，请重试");
    } finally {
      setCreateBusy(false);
    }
  }, [genLayer, createId, createName, createTagline, createSkills, createSchedule, createBoundConnectors, refresh, open]);

  // P2d 左栏 · 真对话 (spec §03): multi-round, client-held (rule 4 — never
  // persisted; the durable outcome is the proposal sidecar). Each send is one
  // bounded assist generation carrying the prior exchanges as context.
  const [assistIntent, setAssistIntent] = useState("");
  const [assistField, setAssistField] = useState<string | null>(null);
  const [assistBusy, setAssistBusy] = useState(false);
  const [assistErrors, setAssistErrors] = useState<string[]>([]);
  const [chatTurns, setChatTurns] = useState<ChatTurn[]>([]);

  // P2d 活文档: which row is drilled open + the evidence payload.
  const [drawerField, setDrawerField] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<EvidenceInfo | null>(null);
  const [openConnector, setOpenConnector] = useState<string | null>(null);

  // P2d-4c: the 团队技能 library opens IN PLACE from the builder surfaces
  // (the 建助手 → 日程页 → 弹窗 two-hop was rejected in walkthrough). After
  // close, fold freshly-uploaded ext skills into the create-flow universe.
  const [skillsOpen, setSkillsOpen] = useState(false);
  const closeSkills = useCallback(async () => {
    setSkillsOpen(false);
    const body = await getJson<{ skills: { tenant: string; name: string }[] }>(
      "/api/scheduler/skills"
    );
    if (body !== null) {
      const extIds = body.skills.map((s) => `ext:${s.tenant}/${s.name}`);
      setSkillUniverse((prev) => [...new Set([...prev, ...extIds])]);
    }
  }, []);

  // P2d-4b lifecycle: 下架 double-confirm + archive/restore actions.
  const [confirmArchive, setConfirmArchive] = useState(false);
  const lifecycle = useCallback(
    async (id: string, action: "archive" | "restore") => {
      const res = await fetch(`/api/profiles/${id}/${action}`, {
        method: "POST",
        headers: authHeaders({ Accept: "application/json" }),
      }).catch(() => null);
      if (res?.ok === true) {
        setNotice(action === "archive" ? "已下架 · 不再服务新对话" : "已恢复上架");
        setConfirmArchive(false);
        await refresh();
        if (action === "archive") navigate("/admin/assistants");
      } else {
        const body = (await res?.json().catch(() => null)) as {
          violations?: { message: string }[];
        } | null;
        setNotice(body?.violations?.map((v) => v.message).join("；") ?? "操作失败，请重试");
      }
    },
    [refresh, navigate]
  );

  /** Compose the assistant's chat reply from the assist outcome (deterministic
      client copy — the trustworthy channel is the 待采纳 chips themselves). */
  const assistReply = (proposals: Proposal[]): string => {
    if (proposals.length === 0) return "看了一下，这个想法不需要改配置——已有的设定已经覆盖了。";
    const items = proposals
      .map((pp) => `「${PROPOSAL_FIELD_NAMES[pp.field] ?? pp.field}」${pp.summary}`)
      .join("；");
    return `提了 ${proposals.length} 个改法：${items}。已标在右侧，采纳哪条点哪条；采纳后进草稿，发布前都能撤。`;
  };

  const sendAssist = useCallback(async () => {
    if (selected === null || assistIntent.trim().length === 0) return;
    const intent = assistIntent;
    setAssistBusy(true);
    setAssistErrors([]);
    setChatTurns((prev) => [...prev, { role: "admin", text: intent }]);
    setAssistIntent("");
    try {
      // The intent should apply to what the admin SEES — save the form as the
      // draft first so the generation context matches the screen.
      await saveDraft();
      const res = await fetch(`/api/profiles/${selected}/assist`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          intent,
          ...(assistField !== null ? { field: assistField } : {}),
          // P2d multi-round: prior exchanges as bounded prompt context.
          history: chatTurns.map((t) => ({ role: t.role, text: t.text.slice(0, 2000) }))
        })
      });
      const body = (await res.json()) as {
        proposals?: Proposal[];
        message?: string;
        violations?: { field: string; message: string }[];
      };
      if (!res.ok) {
        // Out-of-policy intents land here in human language (spec §03 rule 6).
        const msgs =
          body.message !== undefined ? [body.message] : (body.violations ?? []).map(violationCopy);
        setChatTurns((prev) => [...prev, { role: "assistant", text: msgs.join("；") }]);
        return;
      }
      setAssistField(null);
      setChatTurns((prev) => [...prev, { role: "assistant", text: assistReply(body.proposals ?? []) }]);
      const d = await getJson<ProfileDetail>(`/api/profiles/${selected}`);
      setDetail(d);
    } catch {
      setAssistErrors(["没搭出来，换个说法试试"]);
    } finally {
      setAssistBusy(false);
    }
  }, [selected, assistIntent, assistField, chatTurns, saveDraft]);

  const proposalAct = useCallback(
    async (pid: string, action: "adopt" | "dismiss") => {
      if (selected === null) return;
      await fetch(`/api/profiles/${selected}/proposals/${pid}/${action}`, {
        method: "POST",
        headers: authHeaders({ Accept: "application/json" })
      }).catch(() => undefined);
      // Reload REGARDLESS of the outcome: a 404 means the proposal is already
      // gone server-side — the stale chip must leave the screen either way
      // (only-on-200 froze the list after a double-click race).
      await open(selected);
    },
    [selected, open]
  );

  /** Pending proposals for one field (rendered as 待采纳 chips on that row). */
  const proposalsFor = useCallback(
    (field: string): Proposal[] => (detail?.proposals ?? []).filter((pp) => pp.field === field),
    [detail]
  );

  /** 待采纳 chip with inline adopt/dismiss (design §08: 提案必落到具体字段). */
  const proposalChips = (field: string): JSX.Element | null => {
    const items = proposalsFor(field);
    if (items.length === 0) return null;
    return (
      <span className="profiles-proposals" data-testid={`proposal-${field}`}>
        {items.map((pp) => (
          <span key={pp.id} className="profiles-proposal-chip" title={String(pp.after)}>
            {pp.summary} · 待采纳
            <button onClick={() => void proposalAct(pp.id, "adopt")}>采纳</button>
            <button onClick={() => void proposalAct(pp.id, "dismiss")}>放弃</button>
          </span>
        ))}
      </span>
    );
  };

  /**
   * Preview = draft sandbox chat (openspec/agent-builder.md P2a-3): save the
   * current form as the draft first, then run one isolated turn under
   * 草稿 ⊕ 基座 — behavior, not just diff text, before the publish gate.
   */
  const previewSend = useCallback(async () => {
    if (selected === null || previewQ.trim().length === 0) return;
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      await saveDraft();
      const res = await fetch(`/api/profiles/${selected}/preview/chat`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ message: previewQ })
      });
      if (!res.ok) {
        setPreviewError("试聊失败，请稍后再试");
        return;
      }
      const body = (await res.json()) as {
        text: string;
        blocks: { type: string; markdown?: string }[];
        citations: unknown[];
      };
      // First slice renders text (structured answers fall back to their
      // markdown blocks); the full block renderer arrives with the SSE upgrade.
      const text =
        body.text.trim().length > 0
          ? body.text
          : body.blocks
              .filter((b) => b.type === "markdown" && b.markdown !== undefined)
              .map((b) => b.markdown)
              .join("\n\n");
      setPreviewA({ text: text || "（无文本回答）", citations: body.citations.length });
    } catch {
      setPreviewError("试聊失败，请稍后再试");
    } finally {
      setPreviewBusy(false);
    }
  }, [selected, previewQ, saveDraft]);

  const p = detail?.published;
  // P2d evidence blocks flattened for the property rows (null = 未知/不可用).
  const kbEv = evidence?.kb ?? null;
  const spendEv = evidence?.spend ?? null;

  /** M5 发布记录 (§09 canonical) — one block, two homes (legacy card / detail tab). */
  const versionsBlock = (
    <div className="profiles-versions" data-testid="profiles-versions">
      <h4>发布记录</h4>
      {records.length === 0 && <p className="cc-desc">还没有发布记录</p>}
      <ul>
        {records
          .slice()
          .reverse()
          .map((r) => {
            const who = r.publishedRecord?.subjectId ?? "—";
            const diff = r.publishedRecord?.payload.diff ?? [];
            const summary =
              r.publishedRecord?.payload.rollbackOf !== undefined
                ? `回滚自版本 ${r.publishedRecord.payload.rollbackOf}`
                : [...new Set(diff.map((d) => fieldName(d.field)))].join("、") || "—";
            return (
              <li key={r.version}>
                发布 · 版本 {r.version} · {who} — {summary}
                {r.version !== p?.version && (
                  <button onClick={() => void rollback(r.version)}>回滚</button>
                )}
                {diff.length > 0 && (
                  <button
                    onClick={() =>
                      setExpandedVersion(expandedVersion === r.version ? null : r.version)
                    }
                  >
                    {expandedVersion === r.version ? "收起 ↑" : "展开变更 ↓"}
                  </button>
                )}
                {expandedVersion === r.version && (
                  <p className="cc-desc">
                    变更字段：{diff.map((d) => fieldName(d.field)).join("、")}
                  </p>
                )}
              </li>
            );
          })}
      </ul>
    </div>
  );

  /** 运营信号（漏绑 / 预算报警 / 绑定通知）— legacy 卡内 + builder home 横条共用。 */
  const alertsStrip = registry !== null && (
    <>
      {registry.unboundTenants.length > 0 && (
        <p className="cc-desc" data-testid="profiles-unbound">
          检查是否漏绑：{registry.unboundTenants.join("、")} 未绑定助手，当前由默认助手服务
          {registry.unboundTenants.map((t) => (
            <button key={t} className="profiles-bind-edit" onClick={() => openBinding(t)}>
              绑定 {t}
            </button>
          ))}
        </p>
      )}
      {/* P2c 预算报警 (adjudication #1): over-budget assistants surface here
          for admin处置 — alert mode keeps answering, refuse mode has already
          stopped taking new tasks. Absent when metering is off. */}
      {registry.budgets !== undefined && registry.budgets.rows.some((r) => r.overBudget) && (
        <p className="cc-desc profiles-budget-alert" data-testid="profiles-budget-alert">
          预算报警：
          {registry.budgets.rows
            .filter((r) => r.overBudget)
            .map(
              (r) =>
                `${r.tenant} 的「${nameOf(r.profileId)}」今日已花 ¥${r.todaySpendCny.toFixed(2)}，达到上限 ¥${r.dailyBudgetCny}`
            )
            .join("；")}
          （{registry.budgets.enforce === "refuse" ? "已停接新问题" : "仍在正常回答，请尽快处置"}）
        </p>
      )}
      {bindingNotice !== null && <p className="cc-desc profiles-notice">{bindingNotice}</p>}
    </>
  );

  /** M4 binding editor — legacy 卡内 + builder(home 横条 / detail 绑定 tab)共用。 */
  const bindingFormBlock = registry !== null && bindingTenant !== null && (
    <div className="profiles-binding-form" data-testid="profiles-binding-form">
      <h4>绑定「{bindingTenant}」的助手</h4>
      <div className="profiles-binding-picks">
        {(registry.profiles ?? []).map((id) => (
          <label key={id} className="profiles-binding-pick" title={id}>
            <input
              type="checkbox"
              checked={bindingProfiles.includes(id)}
              onChange={() => toggleBindingProfile(id)}
            />
            {nameOf(id)}
          </label>
        ))}
      </div>
      <label className="cc-field">
        默认助手
        <select value={bindingDefault} onChange={(e) => setBindingDefault(e.target.value)}>
          <option value="">请选择</option>
          {bindingProfiles.map((id) => (
            <option key={id} value={id}>
              {nameOf(id)}
            </option>
          ))}
        </select>
      </label>
      {bindingErrors.length > 0 && (
        <div data-testid="profiles-binding-errors">
          {bindingErrors.map((m) => (
            <p key={m} className="cc-desc profiles-violation">
              {m}
            </p>
          ))}
        </div>
      )}
      <div className="profiles-actions">
        <button
          className="primary"
          disabled={bindingProfiles.length === 0 || bindingDefault === ""}
          onClick={() => void saveBinding()}
        >
          保存绑定
        </button>
        <button onClick={() => setBindingTenant(null)}>取消</button>
        {registry.registry[bindingTenant] !== undefined &&
          (confirmUnbind ? (
            <button onClick={() => void unbind()}>确认解除（改由默认助手服务）</button>
          ) : (
            <button onClick={() => setConfirmUnbind(true)}>解除绑定</button>
          ))}
      </div>
    </div>
  );

  /** M4 truth table (tenant → binding) — legacy page + detail 绑定 tab. */
  const registryTableCard = registry !== null && (
    <div className="cc-card profiles-registry" data-testid="profiles-registry">
      <h3>助手绑定与运行状况</h3>
      <table className="cc-table">
        <thead>
          <tr>
            <th>企业</th>
            <th>绑定的助手</th>
            <th>近 7 天对话</th>
            <th>使用默认助手</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(registry.registry).map(([tenant, b]) => (
            <tr key={tenant}>
              <td>{tenant}</td>
              <td title={`${b.profiles.join(", ")} · 默认 ${b.default}`}>
                {b.profiles.map(nameOf).join("、")}（默认 {nameOf(b.default)}）
                <button className="profiles-bind-edit" onClick={() => openBinding(tenant, b)}>
                  编辑
                </button>
              </td>
              <td>{registry.conversations7d[tenant] ?? 0}</td>
              <td>{registry.fallbackCounts[b.default] ?? 0} 次</td>
            </tr>
          ))}
          {Object.keys(registry.registry).length === 0 && (
            <tr>
              <td colSpan={4}>暂无绑定（所有请求由默认助手服务）</td>
            </tr>
          )}
        </tbody>
      </table>
      {alertsStrip}
      {bindingFormBlock}
    </div>
  );

  // P2d-4 IA modes: builder home (cards) / builder detail (tabs) / legacy P1 page.
  const isDetail = builderOn && profileId !== undefined;
  const isHome = builderOn && profileId === undefined;
  const servingOf = (id: string): [string, { profiles: string[]; default: string }][] =>
    registry === null ? [] : Object.entries(registry.registry).filter(([, b]) => b.profiles.includes(id));
  const spendOf = (id: string): number =>
    (registry?.budgets?.rows ?? []).filter((r) => r.profileId === id).reduce((a, r) => a + r.todaySpendCny, 0);

  const DETAIL_TABS: { key: typeof detailTab; label: string }[] = [
    { key: "overview", label: "总览" },
    { key: "connect", label: "连接与技能" },
    { key: "bindings", label: "绑定" },
    { key: "usage", label: "用量" },
    { key: "versions", label: "发布记录" },
  ];

  return (
    <div className="cc-section profiles-section">
      {isDetail ? (
        <div className="cc-section-head bd-scope">
          <p className="cc-desc">
            <button className="profiles-bind-edit" onClick={() => navigate("/admin/assistants")}>
              ← 助手
            </button>
            {/* P2d-4b: 下架 (double confirm; the default fallback can't go) */}
            {list.find((i) => i.id === selected)?.isDefault !== true &&
              (confirmArchive ? (
                <>
                  <button
                    className="profiles-bind-edit"
                    data-testid="profiles-archive-confirm"
                    onClick={() => selected !== null && void lifecycle(selected, "archive")}
                  >
                    确认下架（不再服务新对话，可恢复）
                  </button>
                  <button className="profiles-bind-edit" onClick={() => setConfirmArchive(false)}>
                    取消
                  </button>
                </>
              ) : (
                <button
                  className="profiles-bind-edit"
                  data-testid="profiles-archive-btn"
                  onClick={() => setConfirmArchive(true)}
                >
                  下架…
                </button>
              ))}
          </p>
          <h2>{name}</h2>
          {tagline.length > 0 && <p className="cc-desc">{tagline}</p>}
        </div>
      ) : (
        <div className="cc-section-head">
          <h2>{isHome ? "助手" : "助手配置"}</h2>
          <p className="cc-desc">
            {isHome
              ? "谁在服务、状态如何，一眼可见；点卡片进入助手页"
              : "每个助手的身份、说明与边界；发布后新对话起生效，历史版本全部保留"}
          </p>
        </div>
      )}

      {/* Legacy P1: the M4 table card in its original place. */}
      {!builderOn && registryTableCard}

      {/* Builder home: 运营信号横条 + 助手卡片 (spec P2d-4 §01). The card keeps
          the profiles-registry testid as the continuity anchor for the ops
          signals (budget alert / unbound) that used to live on the M4 card. */}
      {isHome && !creating && (
        <div className="cc-card profiles-registry bd-scope" data-testid="profiles-registry">
          <div className="bd-home-head">
            <h3>助手</h3>
            <button className="k-btn" data-testid="profiles-new-btn" onClick={startCreate}>
              新建助手
            </button>
          </div>
          {alertsStrip}
          {notice !== null && <p className="cc-desc profiles-notice">{notice}</p>}
          {bindingFormBlock}
          <div className="bd-agent-grid">
            {list.filter((item) => item.archived !== true).map((item) => {
              const serving = servingOf(item.id);
              const isDefaultFor = serving.some(([, b]) => b.default === item.id);
              const spend = spendOf(item.id);
              return (
                <button
                  key={item.id}
                  className="profiles-item bd-agent-card"
                  onClick={() => void open(item.id)}
                >
                  <strong>{item.name}</strong>
                  <span className="cc-desc"> 版本 {item.version}{item.isDefault ? " · 默认" : ""}</span>
                  <span className="bd-agent-status">
                    {serving.length > 0
                      ? `服务 ${serving.map(([t]) => t).join("、")}${isDefaultFor ? " · 默认" : ""}`
                      : "未绑定企业 · 尚未服务用户"}
                    {spend > 0 ? ` · 今日 ¥${spend.toFixed(2)}` : ""}
                  </span>
                </button>
              );
            })}
          </div>
          {loadError && (
            <p className="cc-desc">
              加载失败。<button onClick={() => void refresh()}>重试</button>
            </p>
          )}
          {!loadError && list.length === 0 && <p className="cc-desc">还没有助手配置</p>}
          {/* P2d-4b: 已下架区 — 不再服务，可恢复（版本历史全保留） */}
          {list.some((item) => item.archived === true) && (
            <div className="bd-archived" data-testid="profiles-archived">
              <p className="cc-desc">
                已下架（不再服务新对话，版本历史保留）：
                {list
                  .filter((item) => item.archived === true)
                  .map((item) => (
                    <span key={item.id} className="bd-chip" style={{ marginLeft: 8 }}>
                      {item.name}
                      <button
                        className="profiles-bind-edit"
                        onClick={() => void lifecycle(item.id, "restore")}
                      >
                        恢复
                      </button>
                    </span>
                  ))}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Detail tab bar */}
      {isDetail && (
        <div className="bd-tabs bd-scope" data-testid="profiles-detail-tabs">
          {DETAIL_TABS.map((t) => (
            <button
              key={t.key}
              className={detailTab === t.key ? "on" : ""}
              onClick={() => setDetailTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Detail non-overview tabs */}
      {isDetail && detailTab === "connect" && (
        <div className="cc-card bd-scope" data-testid="profiles-tab-connect">
          <h3>连接与技能</h3>
          <PropertyRow
            label="技能"
            value={(p?.skills ?? []).map((s) => {
              const st = detail?.skillStates?.find((x) => x.id === s);
              const blocked = st?.state === "blocked";
              return (
                <span key={s} className={`profiles-skill${blocked ? " blocked" : ""}`}>
                  {skillName(s)}
                  {blocked ? " · 因数据权限未生效" : ""}
                </span>
              );
            })}
            evidence={`${(detail?.skillStates ?? []).filter((s) => s.state === "effective").length} 项生效`}
          />
          {(evidence?.connectors ?? []).map((c) => (
            <PropertyRow
              key={c.id}
              label="连接器"
              value={
                <>
                  <label
                    className="bd-chip"
                    title={c.id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={boundConnectors.includes(c.id)}
                      onChange={() =>
                        setBoundConnectors((prev) =>
                          prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]
                        )
                      }
                    />
                    绑定
                  </label>{" "}
                  {c.label}
                </>
              }
              state={c.state === "connected" ? "ok" : c.state === "expired" ? "warn" : "off"}
              evidence={c.state === "expired" ? "凭证过期" : "展开看权限"}
              onOpen={() => setOpenConnector(openConnector === c.id ? null : c.id)}
              expanded={
                openConnector === c.id ? (
                  <EvidenceLines
                    lines={[
                      { k: "状态", v: c.state === "connected" ? "已连接" : c.state === "expired" ? "凭证已失效" : "状态未知" },
                      { k: "权限（人话）", v: c.permissions },
                      {
                        k: "绑定含义",
                        v:
                          c.kind === "git"
                            ? "绑定后，这个助手的源码问答只读绑定的仓库（多选=并集）；工单目标与任务侧接入随后续片到位"
                            : "绑定后，从这个助手创建的定时任务默认使用它（任务侧接入随后续片到位）",
                      },
                    ]}
                  />
                ) : undefined
              }
            />
          ))}
          {boundConnectors.join(",") !== (p?.connectors ?? []).join(",") && (
            <p className="cc-desc">
              <button
                className="bd-chip"
                data-testid="profiles-connectors-save"
                onClick={() => {
                  void saveDraft();
                  setNotice("连接绑定已存入草稿 · 发布后生效");
                }}
              >
                存入草稿（发布后生效）
              </button>
            </p>
          )}
          <p className="cc-desc">
            <button className="bd-chip" data-testid="profiles-skills-lib" onClick={() => setSkillsOpen(true)}>
              技能库（上传 / 管理外置技能）
            </button>
          </p>
        </div>
      )}
      {isDetail && detailTab === "bindings" && registryTableCard}
      {isDetail && detailTab === "usage" && (
        <div className="cc-card bd-scope" data-testid="profiles-tab-usage">
          <h3>用量</h3>
          <PropertyRow
            label="今日花费"
            value={spendEv !== null ? `¥${spendEv.todayCny.toFixed(2)}` : "未知"}
            evidence={
              registry?.budgets === undefined
                ? "仅记录 · 暂不限制"
                : spendEv?.dailyBudgetCny !== undefined
                  ? `上限 ¥${spendEv.dailyBudgetCny} / 天 · ${registry.budgets.enforce === "refuse" ? "超限拒新任务" : "超限报警"}`
                  : "已计量 · 未设上限"
            }
          />
          {servingOf(selected ?? "").map(([tenant]) => (
            <PropertyRow
              key={tenant}
              label="近 7 天对话"
              value={`${registry?.conversations7d[tenant] ?? 0} 次`}
              evidence={`企业 ${tenant}`}
            />
          ))}
        </div>
      )}
      {isDetail && detailTab === "versions" && <div className="cc-card bd-scope">{versionsBlock}</div>}

      {/* P2d-4c: the shared 团队技能 library, opened in place (no page hop). */}
      {skillsOpen && (
        <SkillsManager
          onUse={() => {
            setSkillsOpen(false);
            window.open("/scheduler", "_blank");
          }}
          onClose={() => void closeSkills()}
        />
      )}

      {/* P2d-2 新建助手 (spec §02): 对话流内的生成式步骤卡，表单分页 stepper 退役 */}
      {builderOn && creating && (
        <div className="cc-card">
          <CreateFlow
            description={createDesc}
            onDescription={setCreateDesc}
            busy={createBusy}
            error={createErr}
            generated={
              genLayer !== null
                ? {
                    name: createName,
                    id: createId,
                    tagline: createTagline,
                    skills: createSkills,
                    wont:
                      ((genLayer.capabilities as { wont?: string[] } | undefined)?.wont ?? []),
                  }
                : null
            }
            onGenerate={() => void genAssistant()}
            onRefine={(text) => void refineCreate(text)}
            step={createStep}
            onStep={setCreateStep}
            onName={setCreateName}
            onId={setCreateId}
            onTagline={setCreateTagline}
            skillUniverse={skillUniverse}
            onToggleSkill={(s) =>
              setCreateSkills((prev) =>
                prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
              )
            }
            skillName={skillName}
            onOpenSkills={() => setSkillsOpen(true)}
            connectors={createConnectors}
            selectedConnectors={createBoundConnectors}
            onToggleConnector={(id) =>
              setCreateBoundConnectors((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            scheduleText={createSchedule}
            onScheduleText={setCreateSchedule}
            scheduleParsed={createScheduleParsed?.label ?? null}
            onPublish={() => void publishCreate()}
            onCancel={() => setCreating(false)}
          />
        </div>
      )}

      {/* Legacy page body OR detail 总览 (the edit surface is one artifact). */}
      {(!builderOn || (isDetail && detailTab === "overview")) && (
      <div className="profiles-body">
        {/* M1: 助手列表 (legacy single page only — the builder home has cards) */}
        {!builderOn && (
        <div className="cc-card profiles-list">
          <h3>助手列表</h3>
          {list.map((item) => (
            <button
              key={item.id}
              className={`profiles-item${selected === item.id ? " active" : ""}`}
              onClick={() => void open(item.id)}
            >
              <strong>{item.name}</strong>
              <span className="cc-desc">
                {" "}
                版本 {item.version}
                {item.isDefault ? " · 默认" : ""}
              </span>
            </button>
          ))}
          {loadError && (
            <p className="cc-desc">
              加载失败。<button onClick={() => void refresh()}>重试</button>
            </p>
          )}
          {!loadError && list.length === 0 && <p className="cc-desc">还没有助手配置</p>}
        </div>
        )}

        {/* M1 right column: 编辑「{name}」 */}
        {selected !== null && (
          <div className="cc-card profiles-form" data-testid="profiles-form">
            {!isDetail && (
              <div className="profiles-form-head">
                <h3>编辑「{name}」</h3>
                {savedAt !== null && <span className="cc-desc">已保存 · {savedAt}</span>}
              </div>
            )}

            <div className={builderOn ? "profiles-split bd-scope" : undefined}>
              {/* P2d 左栏 · 真对话 (spec §03): 多轮往返，提案落到右侧活文档的待采纳态 */}
              {builderOn && (
                <BuilderChat
                  turns={chatTurns}
                  busy={assistBusy}
                  input={assistIntent}
                  onInput={setAssistIntent}
                  onSend={() => void sendAssist()}
                  errors={assistErrors}
                  actions={[
                    { label: "试试这个助手", onClick: () => setPreviewOpen(true) },
                    ...(assistField !== null
                      ? [
                          {
                            label: `仅改「${PROPOSAL_FIELD_NAMES[assistField] ?? assistField}」· 点此取消`,
                            onClick: () => setAssistField(null),
                          },
                        ]
                      : []),
                  ]}
                />
              )}
              <div className={builderOn ? "bd-doc" : "profiles-fields"}>
              {!builderOn && (
              <>

            <h4>名称与简介</h4>
            <label className="cc-field">
              名称
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="cc-field">
              简介（一句话，18 字内）
              <input value={tagline} onChange={(e) => setTagline(e.target.value)} />
            </label>
            <label className="cc-field">
              角色说明
              <textarea rows={8} value={role} onChange={(e) => setRole(e.target.value)} />
            </label>
            <label className="cc-field">
              拿不准时
              <textarea
                rows={2}
                value={whenUnsure}
                onChange={(e) => setWhenUnsure(e.target.value)}
              />
            </label>
            <label className="cc-field">
              输出格式
              <textarea
                rows={2}
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value)}
              />
            </label>
            <label className="cc-field">
              规则
              <textarea rows={3} value={rules} onChange={(e) => setRules(e.target.value)} />
            </label>
            <label className="cc-field">
              边界（不做）· 每行一条 · 只可新增
              <textarea rows={3} value={wont} onChange={(e) => setWont(e.target.value)} />
            </label>

            {/* 来源层 (provenance): inherited/recorded values are first-class,
                shown read-only with their state labels (§09 三态). */}
            {p !== undefined && (
              <div className="profiles-provenance" data-testid="profiles-provenance">
                <p className="cc-desc">
                  能力：
                  {p.skills.map((s) => {
                    const st = detail?.skillStates?.find((x) => x.id === s);
                    const blocked = st?.state === "blocked";
                    return (
                      <span
                        key={s}
                        className={`profiles-skill${blocked ? " blocked" : ""}`}
                        data-skill={s}
                        data-skill-state={st?.state ?? "effective"}
                        {...(blocked ? { title: BLOCKED_SKILL_HINT } : {})}
                      >
                        {skillName(s)}
                        {blocked ? " · 因数据权限未生效" : ""}
                      </span>
                    );
                  })}
                  <span className="profiles-tag">{detail?.provenance?.skills ?? "基座"}</span>
                  <span className="profiles-tag">继承 · 不可移除</span>
                </p>
                <p className="cc-desc">
                  知识范围：
                  {p.kbFilter.sources.length > 0 ? p.kbFilter.sources.join("、") : "未限定"}
                  <span className="profiles-tag">{detail?.provenance?.kbFilter ?? "基座"}</span>
                  <span className="profiles-tag">编辑（只可收窄）</span>
                </p>
                <p className="cc-desc">
                  回答长度上限：
                  {p.capabilities.tokenCaps.maxAnswerTokens !== undefined
                    ? `${p.capabilities.tokenCaps.maxAnswerTokens}`
                    : "未设置"}
                  <span className="profiles-tag">仅记录 · 暂不限制</span>
                </p>
                <p className="cc-desc">
                  花费上限：
                  {p.capabilities.budget?.daily !== undefined ||
                  p.capabilities.budget?.perExecution !== undefined
                    ? [
                        ...(p.capabilities.budget?.daily !== undefined
                          ? [`¥${p.capabilities.budget.daily} / 天`]
                          : []),
                        ...(p.capabilities.budget?.perExecution !== undefined
                          ? [`¥${p.capabilities.budget.perExecution} / 次`]
                          : []),
                      ].join("，")
                    : "未设置"}
                  {/* P2c: the in-place label flip — 仅记录 until metering is live;
                      with metering on, the honest wording depends on whether a
                      cap exists (nothing can trip without one) and the mode. */}
                  {registry?.budgets === undefined ? (
                    <span className="profiles-tag">仅记录 · 暂不限制</span>
                  ) : p.capabilities.budget?.daily === undefined &&
                    p.capabilities.budget?.perExecution === undefined ? (
                    <span className="profiles-tag">已计量 · 未设上限</span>
                  ) : (
                    <span className="profiles-tag">
                      {registry.budgets.enforce === "refuse"
                        ? "已强制 · 超限拒新任务"
                        : "已计量 · 超限报警"}
                    </span>
                  )}
                </p>
                <p className="cc-desc">
                  可见范围：
                  {p.visibility?.minClearance === "internal" ? "仅内部用户可见" : "所有用户可见"}
                  <span className="profiles-tag">编辑（只可收窄）</span>
                </p>
              </div>
            )}

            <div className="profiles-actions">
              <button onClick={() => void saveDraft()}>保存草稿</button>
              <button className="primary" onClick={() => setConfirming(true)}>
                发布…
              </button>
            </div>
              </>
              )}

              {/* P2d 活文档 (spec §03): 合同卡 + 属性行 + 证据 + 下钻，默认只读 */}
              {builderOn && p !== undefined && (
                <div data-testid="profiles-doc">
                  <div className="bd-doc-head">
                    <span
                      className="bd-doc-name"
                      title="点击修改名称与简介"
                      onClick={() => setDrawerField("identity")}
                    >
                      {name}
                    </span>
                    {tagline.length > 0 && <span className="bd-doc-tagline">{tagline}</span>}
                    <span className="bd-doc-ver">
                      v{p.version} · 已发布
                      {changedFields().length > 0 ? ` · 草稿有 ${changedFields().length} 处未发布` : ""}
                      {savedAt !== null ? ` · 已保存` : ""}
                    </span>
                  </div>

                  <ContractCard
                    will={deriveContract({
                      ...(p.capabilities.will !== undefined ? { will: p.capabilities.will } : {}),
                      wont: [],
                      skills: p.skills,
                      kbSources: p.kbFilter.sources,
                    }).will}
                    wont={[
                      ...deriveContract({
                        wont: wont.split("\n").map((w) => w.trim()).filter((w) => w.length > 0),
                        skills: [],
                        kbSources: [],
                        ...(p.capabilities.budget !== undefined ? { budget: p.capabilities.budget } : {}),
                        ...(p.visibility?.minClearance !== undefined
                          ? { minClearance: p.visibility.minClearance }
                          : {}),
                      }).wont.map((text) => ({ text })),
                      // 待采纳的边界新增：提案是整数组替换，新条目单独成行带采纳 chip。
                      ...proposalsFor("capabilities.wont").flatMap((pp) => {
                        const cur = wont.split("\n").map((w) => w.trim());
                        const after = Array.isArray(pp.after) ? pp.after : [String(pp.after)];
                        return after
                          .filter((line) => !cur.includes(line))
                          .map((line) => ({
                            text: line,
                            chip: (
                              <span className="profiles-proposal-chip" title={String(pp.after)}>
                                待采纳
                                <button onClick={() => void proposalAct(pp.id, "adopt")}>采纳</button>
                                <button onClick={() => void proposalAct(pp.id, "dismiss")}>放弃</button>
                              </span>
                            ),
                          }));
                      }),
                    ]}
                  />

                  {(
                    [
                      ["instructions.role", "角色说明", role],
                      ["instructions.whenUnsure", "拿不准时", whenUnsure],
                      ["instructions.outputFormat", "输出格式", outputFormat],
                      ["instructions.rules", "规则", rules],
                    ] as const
                  ).map(([field, label, value]) => (
                    <PropertyRow
                      key={field}
                      label={label}
                      value={
                        <>
                          {value.trim().length > 0 ? (
                            `${value.slice(0, 42)}${value.length > 42 ? "…" : ""}`
                          ) : (
                            <span className="bd-dim">未填写</span>
                          )}
                          {proposalChips(field)}
                        </>
                      }
                      evidence="点击编辑"
                      onOpen={() => setDrawerField(field)}
                      testid={`bd-row-${field}`}
                    />
                  ))}

                  <PropertyRow
                    label="技能"
                    value={p.skills.map((s) => {
                      const st = detail?.skillStates?.find((x) => x.id === s);
                      const blocked = st?.state === "blocked";
                      return (
                        <span
                          key={s}
                          className={`profiles-skill${blocked ? " blocked" : ""}`}
                          data-skill={s}
                          data-skill-state={st?.state ?? "effective"}
                          {...(blocked ? { title: BLOCKED_SKILL_HINT } : {})}
                        >
                          {skillName(s)}
                          {blocked ? " · 因数据权限未生效" : ""}
                        </span>
                      );
                    })}
                    evidence={`${(detail?.skillStates ?? []).filter((s) => s.state === "effective").length} 项生效 · 继承不可移除`}
                  />

                  <PropertyRow
                    label="协作"
                    value={
                      collaborators.length > 0 ? (
                        collaborators.map(nameOf).join("、")
                      ) : (
                        <span className="bd-dim">不协作 · 遇到界外问题只能建议转人工</span>
                      )
                    }
                    evidence="它可自主咨询/转交这些助手 · 点击编辑"
                    onOpen={() => setDrawerField("collaborators")}
                    testid="bd-row-collaborators"
                  />

                  <PropertyRow
                    label="知识范围"
                    value={p.kbFilter.sources.length > 0 ? p.kbFilter.sources.join("、") : "未限定"}
                    evidence={
                      kbEv !== null ? `${kbEv.sources} 来源 · ${kbEv.chunks} 条索引` : "规模未知"
                    }
                    testid="bd-row-kb"
                  />

                  {(evidence?.connectors ?? []).map((c) => (
                    <PropertyRow
                      key={c.id}
                      label="连接器"
                      value={c.label}
                      state={c.state === "connected" ? "ok" : c.state === "expired" ? "warn" : "off"}
                      evidence={c.state === "expired" ? "凭证过期 · 展开看详情" : "展开看权限"}
                      onOpen={() => setOpenConnector(openConnector === c.id ? null : c.id)}
                      testid={`bd-conn-${c.kind}`}
                      expanded={
                        openConnector === c.id ? (
                          <EvidenceLines
                            lines={[
                              {
                                k: "状态",
                                v:
                                  c.state === "connected"
                                    ? `已连接${c.checkedAt !== null ? ` · ${new Date(c.checkedAt).toLocaleString()} 验证` : " · 保存时验证"}`
                                    : c.state === "expired"
                                      ? "凭证已失效，需要重新配置"
                                      : "状态未知",
                              },
                              { k: "权限（人话）", v: c.permissions },
                              { k: "归属", v: "本租户 · 在「系统 · 集成」页配置" },
                            ]}
                          />
                        ) : undefined
                      }
                    />
                  ))}
                  {evidence !== null && evidence.connectors.length === 0 && (
                    <PropertyRow label="连接器" value={<span className="bd-dim">本租户暂无连接器</span>} evidence="在「系统 · 集成」页配置" />
                  )}

                  <PropertyRow
                    label="回答长度"
                    value={
                      <>
                        {p.capabilities.tokenCaps.maxAnswerTokens ?? "未设置"}
                        {proposalChips("capabilities.tokenCaps.maxAnswerTokens")}
                      </>
                    }
                    evidence="仅记录 · 暂不限制"
                  />

                  <PropertyRow
                    label="花费上限"
                    value={
                      p.capabilities.budget?.daily !== undefined ||
                      p.capabilities.budget?.perExecution !== undefined
                        ? [
                            ...(p.capabilities.budget?.daily !== undefined
                              ? [`¥${p.capabilities.budget.daily} / 天`]
                              : []),
                            ...(p.capabilities.budget?.perExecution !== undefined
                              ? [`¥${p.capabilities.budget.perExecution} / 次`]
                              : []),
                          ].join("，")
                        : "未设置"
                    }
                    evidence={
                      registry?.budgets === undefined
                        ? "仅记录 · 暂不限制"
                        : `${
                            p.capabilities.budget?.daily === undefined &&
                            p.capabilities.budget?.perExecution === undefined
                              ? "已计量 · 未设上限"
                              : registry.budgets.enforce === "refuse"
                                ? "已强制 · 超限拒新任务"
                                : "已计量 · 超限报警"
                          }${spendEv !== null ? ` · 今日 ¥${spendEv.todayCny.toFixed(2)}` : ""}`
                    }
                    testid="bd-row-budget"
                  />

                  <PropertyRow
                    label="可见范围"
                    value={p.visibility?.minClearance === "internal" ? "仅内部用户可见" : "所有用户可见"}
                    evidence="编辑（只可收窄）"
                  />

                  {/* 下钻 drawer：原表单控件迁入，存草稿走原端点（乐观锁原样） */}
                  {drawerField === "identity" && (
                    <DrillDrawer
                      title="名称与简介"
                      onSave={() => {
                        void saveDraft();
                        setDrawerField(null);
                      }}
                      onCancel={() => setDrawerField(null)}
                    >
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="名称" />
                      <input
                        value={tagline}
                        onChange={(e) => setTagline(e.target.value)}
                        placeholder="简介（一句话，18 字内）"
                      />
                    </DrillDrawer>
                  )}
                  {(
                    [
                      ["instructions.role", "角色说明", role, setRole, 8],
                      ["instructions.whenUnsure", "拿不准时", whenUnsure, setWhenUnsure, 2],
                      ["instructions.outputFormat", "输出格式", outputFormat, setOutputFormat, 2],
                      ["instructions.rules", "规则", rules, setRules, 3],
                    ] as const
                  ).map(
                    ([field, label, value, setValue, rows]) =>
                      drawerField === field && (
                        <DrillDrawer
                          key={field}
                          title={label}
                          onSave={() => {
                            void saveDraft();
                            setDrawerField(null);
                          }}
                          onCancel={() => setDrawerField(null)}
                          onRewrite={() => {
                            setAssistField(field);
                            setDrawerField(null);
                          }}
                        >
                          <textarea rows={rows} value={value} onChange={(e) => setValue(e.target.value)} />
                        </DrillDrawer>
                      )
                  )}
                  {drawerField === "collaborators" && (
                    <DrillDrawer
                      title="协作"
                      hint="勾选它可以自主咨询或转交的助手；模型按对话上下文决定何时协作，不需要用户 @"
                      onSave={() => {
                        void saveDraft();
                        setDrawerField(null);
                      }}
                      onCancel={() => setDrawerField(null)}
                    >
                      <div className="bd-plan-chips">
                        {list
                          .filter((item) => item.id !== selected && item.published)
                          .map((item) => (
                            <label key={item.id} className="bd-chip" title={item.id}>
                              <input
                                type="checkbox"
                                checked={collaborators.includes(item.id)}
                                onChange={() =>
                                  setCollaborators((prev) =>
                                    prev.includes(item.id)
                                      ? prev.filter((x) => x !== item.id)
                                      : [...prev, item.id]
                                  )
                                }
                              />
                              {nameOf(item.id)}
                            </label>
                          ))}
                      </div>
                    </DrillDrawer>
                  )}
                  {drawerField === "capabilities.wont" && (
                    <DrillDrawer
                      title="边界（不做）"
                      hint="每行一条 · 只可新增"
                      onSave={() => {
                        void saveDraft();
                        setDrawerField(null);
                      }}
                      onCancel={() => setDrawerField(null)}
                      onRewrite={() => {
                        setAssistField("capabilities.wont");
                        setDrawerField(null);
                      }}
                    >
                      <textarea rows={3} value={wont} onChange={(e) => setWont(e.target.value)} />
                    </DrillDrawer>
                  )}
                  {drawerField === null && (
                    <p className="cc-desc">
                      <button className="bd-chip" onClick={() => setDrawerField("capabilities.wont")}>
                        编辑边界
                      </button>
                    </p>
                  )}

                  <div className="bd-doc-actions">
                    <button data-testid="profiles-preview-btn" onClick={() => setPreviewOpen((o) => !o)}>
                      预览（沙箱试聊）
                    </button>
                    <button className="primary" onClick={() => setConfirming(true)}>
                      发布…
                    </button>
                  </div>
                </div>
              )}

            {/* P2 preview panel: 沙箱试聊 (design §08; flag + admin gated) */}
            {builderOn && previewOpen && (
              <div className="cc-card profiles-preview" data-testid="profiles-preview">
                <div className="profiles-preview-head">
                  <h4>预览 · {name}（草稿）</h4>
                  <span className="cc-desc">沙箱试聊 · 不落会话 · 不建工单</span>
                </div>
                <div className="profiles-preview-ask">
                  <input
                    value={previewQ}
                    placeholder="像用户一样问一句…"
                    onChange={(e) => setPreviewQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void previewSend();
                    }}
                  />
                  <button disabled={previewBusy || previewQ.trim().length === 0} onClick={() => void previewSend()}>
                    {previewBusy ? "试聊中…" : previewA === null ? "试一句" : "再试一句"}
                  </button>
                </div>
                {previewError !== null && <p className="cc-desc profiles-violation">{previewError}</p>}
                {previewA !== null && (
                  <div className="profiles-preview-answer" data-testid="profiles-preview-answer">
                    <p>{previewA.text}</p>
                    <p className="cc-desc">
                      引用 {previewA.citations} 条
                      <button className="primary" onClick={() => setConfirming(true)}>
                        看起来没问题，去发布…
                      </button>
                    </p>
                  </div>
                )}
              </div>
            )}
            {notice !== null && <p className="cc-desc profiles-notice">{notice}</p>}
            {violations.length > 0 && (
              <div className="profiles-violations" data-testid="profiles-violations">
                {violations.map((v) => (
                  <p key={v} className="cc-desc profiles-violation">
                    {v}
                  </p>
                ))}
              </div>
            )}

            {/* M3: 发布确认 (§09 canonical) */}
            {confirming && (
              <div className="cc-card profiles-confirm" data-testid="profiles-confirm">
                <h4>发布确认 · {name}</h4>
                {changedFields().length === 0 ? (
                  <p>没有可发布的变更</p>
                ) : (
                  <p>草稿 → 版本 {(p?.version ?? 0) + 1} · 新对话起生效</p>
                )}
                <div className="profiles-actions">
                  <button onClick={() => setConfirming(false)}>返回修改</button>
                  <button
                    className="primary"
                    disabled={changedFields().length === 0}
                    onClick={() => void publish()}
                  >
                    确认发布
                  </button>
                </div>
              </div>
            )}

            {/* M5: 发布记录 — legacy keeps it in place; the builder IA gives it a tab */}
            {!builderOn && versionsBlock}
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
